/**
 * Standard Actions configuration for Dawn.
 *
 * Storefront Renderer injects the Shopify Standard Actions bundle
 * (`window.Shopify.actions.{updateCart,openCart,getCart,…}`). This file
 * overrides the bundle's built-in Dawn refresh path with an explicit,
 * in-theme version so forks that change Dawn's cart contract keep
 * working. Remove this file and the built-in defaults take over.
 *
 *   - openCart   — opens <cart-drawer>; falls back to /cart.
 *   - updateCart — after the Storefront API mutation, refreshes the
 *     affected cart sections and publishes `cart-update` so Dawn's
 *     pubsub subscribers react.
 *   - other actions (getCart, etc.) keep the default implementation.
 */

const DAWN_CART_TAGS = ['cart-drawer', 'cart-items', 'cart-drawer-items', 'cart-notification'];
const DAWN_PUBSUB_REFRESHED_SECTIONS = new Set([
  'cart-drawer:cart-drawer',
  'cart-drawer-items:CartDrawer',
  'cart-items:main-cart-items',
]);

function collectCartSections() {
  const sections = new Map();
  for (const el of document.querySelectorAll(DAWN_CART_TAGS.join(','))) {
    let entries;
    try { entries = el.getSectionsToRender?.(); } catch { continue; }
    const tag = el.tagName.toLowerCase();
    for (const entry of entries ?? []) {
      if (DAWN_PUBSUB_REFRESHED_SECTIONS.has(`${tag}:${entry.id}`)) continue;
      const sectionId = entry.section ?? entry.id;
      if (!sectionId || sections.has(sectionId)) continue;
      const root = entry.section ? document.getElementById(entry.id) : document;
      if (!root) continue;
      const mount = entry.selector ? (root.querySelector(entry.selector) ?? (entry.section ? root : null)) : document.getElementById(entry.id);
      if (!mount) continue;
      sections.set(sectionId, { mount, extractSelector: entry.selector || '.shopify-section' });
    }
  }
  return sections;
}

async function refreshDawnCartUI() {
  const sections = collectCartSections();
  const sectionsQuery = sections.size ? `?sections=${[...sections.keys()].join(',')}` : '';
  const cartUrl = (typeof routes !== 'undefined' && routes?.cart_url) || '/cart';
  const cartData = await fetch(`${cartUrl}.js${sectionsQuery}`, { headers: { Accept: 'application/json' } })
    .then((r) => (r.ok ? r.json() : null)).catch(() => null);
  if (cartData?.sections) {
    for (const [id, { mount, extractSelector }] of sections) {
      const html = cartData.sections[id];
      if (!html) continue;
      const source = new DOMParser().parseFromString(html, 'text/html').querySelector(extractSelector);
      if (source) mount.replaceChildren(...source.childNodes);
    }
  }
  publish(PUB_SUB_EVENTS.cartUpdate, { source: 'external-refresh', cartData: cartData ?? undefined });
}

function initStandardActions() {
  const actions = window.Shopify?.actions;
  if (!actions) return;
  actions.openCart.configure({
    async handler(defaultHandler) {
      const drawer = document.querySelector('cart-drawer');
      if (drawer && typeof drawer.open === 'function') { drawer.open(); return; }
      return defaultHandler();
    },
  });
  actions.updateCart.configure({
    eventTarget: () => document,
    async handler(defaultHandler) {
      const result = await defaultHandler();
      try { await refreshDawnCartUI(); }
      catch (error) { console.error('[Dawn] Standard Actions cart refresh failed; reloading.', error); window.location.reload(); }
      return result;
    },
  });
}

if (window.Shopify?.actions) initStandardActions();
else document.addEventListener('DOMContentLoaded', initStandardActions, { once: true });

/* ZEYRAVO orbit hover-swap enhancement.
   Desktop only: when the active/front product card is hovered, swap its first
   product image for the product's second media image. Nothing else in the
   orbit interaction is changed. Trending cards already have this behavior
   in novixa-product-card.liquid, so they are skipped. */
(() => {
  const SELECTORS = [
    '.novixa-orbit__card.is-active',
    '.novixa-best-orbit__card.is-active',
    '.novixa-trending__orbit-card.is-active',
  ].join(',');
  const cache = new Map();

  const isDesktop = () => window.matchMedia('(min-width: 750px)').matches;

  function getProductJson(href) {
    try {
      const url = new URL(href, window.location.origin);
      url.search = '';
      const key = url.pathname;
      if (cache.has(key)) return cache.get(key);
      const promise = fetch(`${url.origin}${url.pathname}.js`, { headers: { Accept: 'application/json' } })
        .then((response) => response.ok ? response.json() : null)
        .catch(() => null);
      cache.set(key, promise);
      return promise;
    } catch { return Promise.resolve(null); }
  }

  function addSwap(card) {
    if (!card || card.dataset.orbitHoverSwap === '1' || !isDesktop()) return;
    if (card.closest('.novixa-trending') && card.querySelector('.nx-card__image--secondary')) return;
    const link = card.matches('a[href]') ? card : card.querySelector('a[href]');
    const target = card.querySelector('.novixa-orbit__media, .novixa-best-orbit__media, .nx-card__media');
    if (!link || !target) return;
    card.dataset.orbitHoverSwap = '1';

    let overlay = null;
    let ready = false;
    let hovering = false;

    const show = () => {
      if (!ready || !overlay) return;
      hovering = true;
      overlay.style.opacity = '1';
      const canvas = target.querySelector('canvas');
      const front = target.querySelector('.novixa-orbit__image, canvas');
      if (front && !front.classList?.contains('nx-card__image--secondary')) front.style.opacity = '0';
    };
    const hide = () => {
      hovering = false;
      if (!overlay) return;
      overlay.style.opacity = '0';
      const canvas = target.querySelector('canvas');
      const front = target.querySelector('.novixa-orbit__image, canvas');
      if (front && !front.classList?.contains('nx-card__image--secondary')) front.style.opacity = '';
    };

    card.addEventListener('mouseenter', async () => {
      if (!isDesktop()) return;
      const data = await getProductJson(link.href);
      if (!data?.images?.[1] || card.classList.contains('is-active') === false) return;
      if (!overlay) {
        overlay = document.createElement('img');
        overlay.className = 'novixa-orbit__hover-secondary-image';
        overlay.src = data.images[1];
        overlay.alt = '';
        overlay.setAttribute('aria-hidden', 'true');
        Object.assign(overlay.style, {
          position: 'absolute', inset: '0', width: '100%', height: '100%',
          objectFit: target.classList.contains('novixa-best-orbit__media') ? 'contain' : 'contain',
          background: 'inherit', opacity: '0', transition: 'opacity .22s ease',
          pointerEvents: 'none', zIndex: '3'
        });
        if (getComputedStyle(target).position === 'static') target.style.position = 'relative';
        target.appendChild(overlay);
      }
      ready = true;
      show();
    });
    card.addEventListener('mouseleave', hide);
  }

  const scan = () => document.querySelectorAll(SELECTORS).forEach(addSwap);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', scan, { once: true }); else scan();

  const observer = new MutationObserver(scan);
  observer.observe(document.documentElement, { childList: true, subtree: true });
})();